<template>
  <div class="article__reactions px-4 sm:px-0 content-visibility-reactions">
    <header class="mb-6 flex items-center">
      <OIcon icon="category/eglence" svg-class="w-6 h-6 sm:w-8 sm:h-8" />
      <span class="font-semibold text-md sm:text-20 mx-2">{{ $t('main.reaction-text') }}</span>
      <div class="border-t border-[#E2E5ED] flex-grow"></div>
    </header>
    <div class="reaction-list flex space-x-2 sm:space-x-6">
      <div v-for="(reaction, index) in reactions" :id="reaction.id" :key="reaction.id" class="reaction-wrapper w-[42px] sm:w-[62px] h-full">
        <div class="relative h-24 sm:h-[142px]">
          <div class="absolute inset-0 z-0 flex flex-col items-center justify-end">
            <div
              class="reaction-item"
              :class="`w-full transition-all duration-200 rounded-full ${
                reaction.newHeight || reaction.isActive ? 'bg-[#34D399]' : 'bg-[#E2E5ED]'
              } ${reaction.isActive ? 'reaction-item-active' : ''}`"
              :style="{ height: `calc(${reaction.height}% + ${reaction.offset}px)` }"
            ></div>
          </div>
          <OButton
            pure
            :disabled="isReactionsDisabled"
            class="absolute bottom-0 z-0 bg-white rounded-full"
            :class="`w-full ${isReactionsDisabled ? 'cursor-wait' : ''}`"
            aria-label="tepki"
            @click="handleReaction(reaction, index)"
          >
            <div class="h-[42px] w-[42px] sm:h-[62px] sm:w-[62px] bg-white rounded-full u-center border border-[#E2E5ED]}">
              <div class="reaction u-center h-[28.45px] w-[28.45px] sm:h-[42px] sm:w-[42px]">
                <div class="w-full h-full relative">
                  <div style="padding-bottom: 100%"></div>
                  <OImage loading="lazy" :src="reaction.icons.gif || reaction.icons.svg" class="absolute inset-0 h-auto w-full" alt="" />
                </div>
              </div>
            </div>
          </OButton>
        </div>
        <div
          class="font-normal text-primary-300 text-13 sm:text-md text-center mt-2 leading-16"
          :class="{ 'font-bold': reaction.isActive }"
        >
          {{ reaction.count }}
        </div>
      </div>
    </div>
    <div v-if="showWarning" class="text-red-500 text-center text-sm -ml-3 mt-4">{{ $t('news-page.emoji-reaction-limit') }}</div>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, useContext, inject } from '@nuxtjs/composition-api';
import { sendReactionCommentLayer } from '@/modules/tracking/scripts/dataLayer';
import { useDialog } from '~/composables/useModal';
import { useDevice } from '~/composables/useDevice';
import { useAuth } from '~/modules/auth/state/useAuth';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { OButton, OImage, OIcon } from '~/modules/common/components/ui';

export default defineComponent({
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  components: { OButton, OImage, OIcon },
  setup(props) {
    const { $service, $analyticsEvent } = useContext();
    const { openErrorDialog } = useDialog();
    const { isMobile } = useDevice();
    const { auth } = useAuth();
    const adConfig = inject('adConfig');
    const userId = auth.value.isLoggedIn ? auth.value.user._id : 'anonym';
    const storageUserKey = `u${userId}`;

    const userReactions = ref({});
    const isReactionsDisabled = ref(false);
    const articleId = props.item.legacyId;

    const showWarning = ref(false); // computed refactor

    onMounted(() => {
      userReactions.value = localStore.getItem('o-rcts');
      if (!userReactions.value) {
        userReactions.value = {};
        localStore.setItem('o-rcts', userReactions.value);
      }
      if (!userReactions.value[storageUserKey]) {
        userReactions.value[storageUserKey] = {};
        localStore.setItem('o-rcts', userReactions.value);
      }
    });

    const getCurrentUserReactions = () => {
      const result = localStore.getItem('o-rcts')?.[storageUserKey]?.[articleId];
      return result || [];
    };

    const addReactionStorage = (id) => {
      const newReactions = [...getCurrentUserReactions(), id];
      userReactions.value[storageUserKey][articleId] = newReactions;
      localStore.setItem('o-rcts', userReactions.value);
    };

    const removeReactionStorage = (id) => {
      const newReactions = getCurrentUserReactions().filter((itemId) => itemId !== id);
      userReactions.value[storageUserKey][articleId] = newReactions;
      localStore.setItem('o-rcts', userReactions.value);
    };

    const reactions = computed(() => {
      const allReactions = props.item.reactions;
      // toplam reaction sayısı
      const totalCount = allReactions.reduce((total, reaction) => {
        total += reaction.count;
        return total;
      }, 0);

      allReactions.map((reaction) => {
        reaction.offset = isMobile.value ? 42 : 62;
        // her bir reaction sayısı ile toplam reactıon sayısına bölüp 100 ile çarparak, bir reaction'ın(emojinin) genel oyların yüzde kaçına sahip olduğunu hesaplıyoruz
        reaction.height = totalCount ? (reaction.count / totalCount) * 100 : 0;
        if (reaction.height < 1) reaction.height = 1; // eğer %1'den küçükse 1% height veriyoruz height'ın çok ince olmaması için

        // reaction daha önce verilmiş mi?
        if (process.client) reaction.isActive = getCurrentUserReactions().includes(reaction.id);

        return reaction;
      });

      return allReactions;
    });

    const handleReaction = async (reaction, index) => {
      isReactionsDisabled.value = true;
      try {
        if (getCurrentUserReactions().includes(reaction.id)) {
          if (getCurrentUserReactions().length <= 3) {
            showWarning.value = false;
          }
          await $service.article.removeReaction(props.item._id, reaction.id);
          reaction.count -= 1;
          reaction.newHeight = null; // bar'ı eski boyutuna getir
          removeReactionStorage(reaction.id);
        } else {
          if (getCurrentUserReactions().length === 3) {
            showWarning.value = true;
            return;
          }
          await $service.article.addReaction(props.item._id, reaction.id);
          reaction.count += 1;
          reaction.newHeight = reaction.height + 6; // bar'ı büyüt
          addReactionStorage(reaction.id);
          sendReactionCommentLayer('reaction', { article: props.item, adConfig }, reaction);
          const urlSplit = reaction.icons.svg.split('/');
          const reactionType = urlSplit[urlSplit.length - 1].split('.')[0];
          $analyticsEvent('Detailpage', 'User_Engagement', `detailpage_emoji_${reactionType}`);
        }
      } catch (error) {
        openErrorDialog({ error });
      } finally {
        isReactionsDisabled.value = false;
      }
    };
    return { reactions, handleReaction, showWarning, isReactionsDisabled };
  },
});
</script>

<style lang="scss">
.content-visibility-reactions {
  contain-intrinsic-size: 100px 120px;
  content-visibility: auto;
  @include sm() {
    content-visibility: visible;
  }
}
</style>
